import {
  collection,
  deleteField,
  doc,
  getDocs,
  onSnapshot,
  query,
  runTransaction,
  setDoc,
  Timestamp,
  where,
  writeBatch,
} from "firebase/firestore";
import { db } from "./firebase";
import {
  ActiveTimer,
  ActiveTimersMap,
  TimeEntry,
  TimeEntryHistoryEntry,
} from "../@types/timeEntry";
import { TeamMember } from "../@types/team";
import { generateUid } from "./general";

const timeEntriesCollection = (userId: string) =>
  collection(db, "users", userId, "timeEntries");

const activeTimersDoc = (userId: string) =>
  doc(db, "users", userId, "metadata", "activeTimers");

// ---- Active timers (the running clocks) ----

export const subscribeToActiveTimers = (
  userId: string,
  onUpdate: (timers: ActiveTimersMap) => void,
  onError?: (error: Error) => void
) => {
  return onSnapshot(
    activeTimersDoc(userId),
    (snap) => {
      onUpdate(snap.exists() ? (snap.data() as ActiveTimersMap) : {});
    },
    (error) => onError?.(error as Error)
  );
};

export async function startTimer(
  userId: string,
  teamMember: TeamMember,
  jobUid: string,
  jobId: number
): Promise<void> {
  const timer: ActiveTimer = {
    jobUid,
    jobId,
    startedAt: Timestamp.now(),
    teamMember,
  };
  // Explicitly clear lastNotifiedAt and pause state: merge:true deep-merges
  // map entries, so stale fields surviving from a previous session (e.g. a
  // markTimerNotified write racing a stop) would otherwise carry over and
  // skew the new timer's baselines.
  await setDoc(
    activeTimersDoc(userId),
    {
      [teamMember.uid]: {
        ...timer,
        lastNotifiedAt: deleteField(),
        pausedAt: deleteField(),
        pausedMs: 0,
      },
    },
    { merge: true }
  );
}

// Worked time on a running timer: wall clock minus completed pauses minus the
// in-progress pause (if any). This is the number that gets logged and costed.
export const getWorkedMs = (timer: ActiveTimer, nowMs: number): number => {
  const paused =
    (timer.pausedMs ?? 0) +
    (timer.pausedAt ? Math.max(0, nowMs - timer.pausedAt.toMillis()) : 0);
  return Math.max(0, nowMs - timer.startedAt.toMillis() - paused);
};

export async function pauseTimer(
  userId: string,
  teamMemberUid: string
): Promise<void> {
  await runTransaction(db, async (tx) => {
    const ref = activeTimersDoc(userId);
    const snap = await tx.get(ref);
    const timer = snap.exists()
      ? (snap.data() as ActiveTimersMap)[teamMemberUid]
      : undefined;
    // No-op if the timer was stopped meanwhile or is already paused (a second
    // tab double-pausing must not reset pausedAt and shrink the pause).
    if (!timer?.startedAt || timer.pausedAt) return;
    tx.set(
      ref,
      { [teamMemberUid]: { pausedAt: Timestamp.now() } },
      { merge: true }
    );
  });
}

export async function resumeTimer(
  userId: string,
  teamMemberUid: string
): Promise<void> {
  await runTransaction(db, async (tx) => {
    const ref = activeTimersDoc(userId);
    const snap = await tx.get(ref);
    const timer = snap.exists()
      ? (snap.data() as ActiveTimersMap)[teamMemberUid]
      : undefined;
    if (!timer?.startedAt || !timer.pausedAt) return;
    const pausedMs =
      (timer.pausedMs ?? 0) +
      Math.max(0, Date.now() - timer.pausedAt.toMillis());
    tx.set(
      ref,
      { [teamMemberUid]: { pausedAt: deleteField(), pausedMs } },
      { merge: true }
    );
  });
}

// Abandon a running timer without logging an entry.
export async function discardTimer(
  userId: string,
  teamMemberUid: string
): Promise<void> {
  await setDoc(
    activeTimersDoc(userId),
    { [teamMemberUid]: deleteField() },
    { merge: true }
  );
}

// Atomically claim the next reminder slot. Returns true only for the client
// that wins the claim, so multiple open tabs/devices show one notification per
// interval instead of one each (the transaction re-checks lastNotifiedAt
// against the latest committed state before advancing it).
export async function claimTimerReminder(
  userId: string,
  teamMemberUid: string,
  intervalMs: number
): Promise<boolean> {
  const ref = activeTimersDoc(userId);
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const timer = snap.exists()
      ? (snap.data() as ActiveTimersMap)[teamMemberUid]
      : undefined;
    if (!timer?.startedAt) return false; // timer was stopped meanwhile
    if (timer.pausedAt) return false; // no work reminders while paused
    const startedMs = timer.startedAt.toMillis();
    // Ignore a pre-start lastNotifiedAt (stale leftover from a prior session).
    const referenceMs = Math.max(
      startedMs,
      timer.lastNotifiedAt?.toMillis() ?? startedMs
    );
    if (Date.now() - referenceMs < intervalMs) return false; // already claimed
    tx.set(
      ref,
      { [teamMemberUid]: { lastNotifiedAt: Timestamp.now() } },
      { merge: true }
    );
    return true;
  });
}

// Stop the clock and log the entry atomically so a timer can never be cleared
// without its entry landing (or vice versa).
export async function stopTimerAndLog(
  userId: string,
  teamMemberUid: string,
  data: Omit<TimeEntry, "uid" | "createdAt">
): Promise<TimeEntry> {
  const uid = generateUid();
  const entry: TimeEntry = {
    ...data,
    uid,
    createdAt: Timestamp.now(),
  };
  const batch = writeBatch(db);
  batch.set(doc(timeEntriesCollection(userId), uid), entry);
  batch.set(
    activeTimersDoc(userId),
    { [teamMemberUid]: deleteField() },
    { merge: true }
  );
  await batch.commit();
  return entry;
}

// ---- Logged time entries ----

export async function getTimeEntries(
  userId: string,
  jobUid?: string
): Promise<TimeEntry[]> {
  const base = timeEntriesCollection(userId);
  const snapshot = await getDocs(
    jobUid ? query(base, where("jobUid", "==", jobUid)) : base
  );
  return (
    snapshot.docs
      .map((d) => d.data() as TimeEntry)
      // Soft-deleted entries are kept for the audit trail but hidden everywhere.
      .filter((e) => !e.deletedAt)
      .sort((a, b) => b.startedAt.toMillis() - a.startedAt.toMillis())
  );
}

export type EditableTimeEntryFields = {
  startedAt: Timestamp;
  endedAt: Timestamp;
  durationMinutes: number;
  // Not hand-editable, always the clock-recorded value, clamped by the modal
  // when a hand-edited window shrinks below the recorded pause.
  pausedMinutes: number;
  cost: number | null;
  lineItemUid: string | null;
  mount: TimeEntry["mount"];
  stage: string | null;
  note: string;
};

const formatDuration = (minutes: number) => {
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

// Diff the editable fields so the history entry records what actually changed.
function diffTimeEntry(
  before: TimeEntry,
  after: EditableTimeEntryFields
): TimeEntryHistoryEntry["changes"] {
  const changes: TimeEntryHistoryEntry["changes"] = [];
  if (before.startedAt.toMillis() !== after.startedAt.toMillis()) {
    changes.push({
      field: "Started",
      before: before.startedAt.toDate().toLocaleString(),
      after: after.startedAt.toDate().toLocaleString(),
    });
  }
  if (before.endedAt.toMillis() !== after.endedAt.toMillis()) {
    changes.push({
      field: "Ended",
      before: before.endedAt.toDate().toLocaleString(),
      after: after.endedAt.toDate().toLocaleString(),
    });
  }
  if (before.durationMinutes !== after.durationMinutes) {
    changes.push({
      field: "Duration",
      before: formatDuration(before.durationMinutes),
      after: formatDuration(after.durationMinutes),
    });
  }
  if ((before.pausedMinutes ?? 0) !== after.pausedMinutes) {
    changes.push({
      field: "Paused",
      before: formatDuration(before.pausedMinutes ?? 0),
      after: formatDuration(after.pausedMinutes),
    });
  }
  if ((before.lineItemUid ?? null) !== (after.lineItemUid ?? null)) {
    const label = (mount: TimeEntry["mount"]) =>
      mount ? `${mount.species ?? "Unknown"} · ${mount.frontFacingId}` : null;
    changes.push({
      field: "Mount",
      before: label(before.mount),
      after: label(after.mount),
    });
  }
  if ((before.stage ?? null) !== (after.stage ?? null)) {
    changes.push({
      field: "Stage",
      before: before.stage ?? null,
      after: after.stage ?? null,
    });
  }
  if ((before.note ?? "") !== (after.note ?? "")) {
    changes.push({
      field: "Note",
      before: before.note || null,
      after: after.note || null,
    });
  }
  if ((before.cost ?? null) !== (after.cost ?? null)) {
    changes.push({
      field: "Cost",
      before: before.cost !== null ? `$${before.cost.toFixed(2)}` : null,
      after: after.cost !== null ? `$${after.cost.toFixed(2)}` : null,
    });
  }
  return changes;
}

export async function updateTimeEntry(
  userId: string,
  entryUid: string,
  updates: EditableTimeEntryFields,
  teamMember?: TeamMember
): Promise<void> {
  const ref = doc(timeEntriesCollection(userId), entryUid);
  // Transaction so concurrent edits can't clobber each other's field changes
  // or drop each other's history entries (the history append is a full-array
  // rewrite, which last-write-wins outside a transaction).
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error("Time entry not found.");
    const current = snap.data() as TimeEntry;

    const changes = diffTimeEntry(current, updates);
    const historyEntry: TimeEntryHistoryEntry = {
      at: Timestamp.now(),
      ...(teamMember ? { teamMember } : {}),
      changes,
    };

    tx.set(
      ref,
      {
        startedAt: updates.startedAt,
        endedAt: updates.endedAt,
        durationMinutes: updates.durationMinutes,
        pausedMinutes: updates.pausedMinutes,
        cost: updates.cost,
        lineItemUid: updates.lineItemUid,
        mount: updates.mount,
        stage: updates.stage,
        note: updates.note,
        updatedAt: Timestamp.now(),
        ...(changes.length
          ? { history: [...(current.history ?? []), historyEntry] }
          : {}),
      },
      { merge: true }
    );
  });
}

// Soft delete: keep the record (with who/when) for the audit trail; it's
// filtered out of every view and total via getTimeEntries.
export async function deleteTimeEntry(
  userId: string,
  entryUid: string,
  teamMember?: TeamMember
): Promise<void> {
  await setDoc(
    doc(timeEntriesCollection(userId), entryUid),
    {
      deletedAt: Timestamp.now(),
      ...(teamMember ? { deletedBy: teamMember } : {}),
    },
    { merge: true }
  );
}
